/* eslint-disable @typescript-eslint/naming-convention */
import { LIST_PARAMS_REQUIRED } from '../constants';
import { TIDJWTUser } from '../types/TIDJWTUser';
import { TIDUser } from '../types/TIDUser';

/**
 * Remove the search params from the url
 * @param {string} url - Url to remove the search params
 * @return {string} - Url without the search params
 * @example
 * removeSearchParams('https://example.com/test?state=fake_state&another_prop=fake_prop')
 * // will return https://example.com/test
 * @example
 * removeSearchParams('https://example.com/test')
 * // will return https://example.com/test
 * @example
 * removeSearchParams('https://example.com/test?state=fake_state&another_prop=fake_prop#hash')
 * // will return https://example.com/test
 */
export const removeSearchParams = (url: string): string => {
  return url.split('?')[0];
};

/**
 * Extract the params from the URL.
 * @param {string} url - Url to extract information
 * @return {string} - Params of the url
 * @example
 * getQueryParams('https://example.com/test?state=fake_state&another_prop=fake_prop')
 * // will return ?state=fake_state&another_prop=fake_prop
 */
export const getQueryParams = (url: string): string => {
  return url.split('?')[1];
};

/**
 * Receives an url and converted into URLSearchParams
 * @param {string} url - Url to extract information
 * @return {URLSearchParams} - URLSearchParams containing the parameters from the URL
 * @example Full url
 * getSearchParams('https://example.com/test?state=fake_state&another_prop=fake_prop')
 * @example Only params
 * getSearchParams('?state=fake_state&another_prop=fake_prop')
 */
export const getSearchParams = (url: string): URLSearchParams => {
  let searchParams = url;
  if (!url.startsWith('?')) {
    try {
      searchParams = new URL(url).search;
    } catch (e) {}
  }
  return new URLSearchParams(searchParams);
};

/**
 * Compare if the url contains the necessary information for the authentication
 *
 * Note: The required parameters that the url should contain are the following:
 * * code
 * * state
 * * identity_provider
 * @param redirectUrl - If the redirect url is provided, it will check if the url starts with the redirect url provided. If not, it will return false
 * @param {string} url - Url to determined if it has the requirement auth params. By default, takes the url of the browser
 * @return {boolean} - True or false if the url is valid
 * @example No url
 * hasAuthParams() // This will take the url from the browser
 * @example Valid url
 * getSearchParams('https://example.com/test?code=fake_code&state=fake_state')
 * // return true
 * @example Invalid url
 * getSearchParams('https://example.com/test?code=fake_code')
 * // return true
 */
export const hasAuthParams = (
  url: string = window.location.href,
  redirectUrl?: string
): boolean => {
  if (redirectUrl != null) {
    const cleanRedirectUrl = removeSearchParams(redirectUrl);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const cleanUrl = removeSearchParams(url ?? '');
    // check if the url matches the redirect url
    if (cleanRedirectUrl !== cleanUrl) {
      return false;
    }
  }
  const searchParams = getSearchParams(url);
  for (const paramRequired of LIST_PARAMS_REQUIRED) {
    if (!searchParams.has(paramRequired)) {
      return false;
    }
  }
  return true;
};

/**
 * Converts the raw JWT user returned from TID and converted into a TIDUser
 * @param {TIDJWTUser} jwtUser - TIDJWTUser to convert
 * @return {TIDUser} - TIDJWTUser converted into a TIDUser
 */
export const transformToTIDUser = (jwtUser: TIDJWTUser): TIDUser => {
  return {
    name: `${jwtUser.given_name} ${jwtUser.family_name}`,
    given_name: jwtUser.given_name,
    family_name: jwtUser.family_name,
    picture: jwtUser.picture,
    email: jwtUser.email,
    email_verified: jwtUser.email_verified,
    phone_number_verified: false,
  };
};
